import { useMutation, useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { z } from 'zod'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { handleInternalAPIResponse } from '@omnea-app/packages/universal/general/observability/handleInternalAPIResponse'
import { ServiceUrls } from '@omnea-app/packages/universal/general/serviceUrls'
import { RequestPreferences, UserPreferencesResponseSchema } from '@omnea-app/users/usersFrontendClient'
import { UsersHttpClient } from '@omnea-app/users/usersHttpClient'

export const useUserRequestPreferences = (requestId: string) => {
  const { user, getToken } = useAuthenticatedUser()
  const { enqueueSnackbar } = useSnackbar()

  type Preferences = z.infer<typeof UserPreferencesResponseSchema>
  const {
    data,
    isLoading: isQueryLoading,
    isError: isQueryError,
    error: queryError,
    refetch,
  } = useQuery<Preferences, Error>({
    queryKey: ['userRequestPreferences', user.id, requestId],
    queryFn: async () => {
      const token = await getToken()
      return fetch(`${ServiceUrls.USERS}/${user.id}/preferences?requestId=${requestId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then(handleInternalAPIResponse)
    },
  })

  const {
    mutate,
    isPending: isMutateLoading,
    isError: isMutateError,
    error: mutateError,
  } = useMutation<RequestPreferences, Error, RequestPreferences>({
    mutationFn: async (requestPreferences) => {
      const token = await getToken()
      const { data, error } = await new UsersHttpClient({
        omneaJwt: token,
      }).POST('/{userId}/request-preferences', {
        body: { requestPreferences },
        params: { path: { userId: user.id } },
      })
      throwIfApiError(error)
      return data.requestPreferences
    },
    onSuccess: () => {
      void refetch()
    },
  })

  const error = queryError || mutateError
  const isError = (_error: Error | null): _error is Error => {
    return isQueryError || isMutateError
  }

  useEffect(() => {
    if (isError(error)) {
      enqueueSnackbar(error?.message, { variant: 'error' })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, error])

  const hasNotificationsEnabled =
    data?.notificationPreferences?.channels?.email ||
    data?.notificationPreferences?.channels?.slack ||
    data?.notificationPreferences?.channels?.teams

  return {
    isMuted: data?.requestPreferences?.isMuted ?? false,
    isLoading: isQueryLoading || isMutateLoading,
    hasNotificationsEnabled: hasNotificationsEnabled ?? true,
    shouldUnmute: (isUnmuted: boolean) => mutate({ id: requestId, isMuted: !isUnmuted }),
  }
}
